import React, { useCallback, useEffect, useState } from "react";
import { Search, User, Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { sanitizeSearchInput } from "../utils/searchUtils";

const hasStoredUserSession = () => {
  try {
    return localStorage.getItem("userAuthenticated") === "true";
  } catch {
    return false;
  }
};

const Navbar = () => {
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const getQueryValue = useCallback(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("q") || "";
  }, [location.search]);

  const isEventDetailsPage = location.pathname.startsWith("/event/");
  const isEventsListContext = location.pathname.startsWith("/events");
  const isTicketsContext = location.pathname.startsWith("/tickets");
  const hasUserSession = user || hasStoredUserSession();

  useEffect(() => {
    if (isEventsListContext || isTicketsContext) {
      setSearchValue(getQueryValue());
    }
  }, [getQueryValue, isEventsListContext, isTicketsContext]);

  useEffect(() => {
    if (isEventDetailsPage) {
      setMobileSearchOpen(false);
    }
  }, [isEventDetailsPage]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });

    return () => subscription?.unsubscribe();
  }, []);

  // ACTIVE CHECK (UPDATED)
  const isActive = (path) => location.pathname === path;

  const isEventsActive =
    location.pathname === "/events" ||
    location.pathname.startsWith("/event"); // 👈 THIS FIX

  const commitSearchToRoute = (nextRawValue, options = {}) => {
    const { replace = true } = options;
    const sanitized = sanitizeSearchInput(nextRawValue);
    const targetPath = isTicketsContext ? "/tickets" : "/events";

    if (sanitized.isEmpty) {
      navigate(targetPath, { replace });
      return;
    }

    navigate(`${targetPath}?q=${encodeURIComponent(sanitized.cleaned)}`, { replace });
  };

  const handleSearchChange = (e) => {
    const nextRawValue = e.target.value;
    setSearchValue(nextRawValue);

    if (isEventsListContext || isTicketsContext) {
      commitSearchToRoute(nextRawValue, { replace: true });
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    commitSearchToRoute(searchValue, { replace: false });
    setMobileSearchOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-brand-maroon/95 backdrop-blur border-b border-white/10">

      <div className="max-w-[1440px] mx-auto px-2 sm:px-4 lg:px-6 h-16 flex items-center justify-between lg:grid lg:grid-cols-3">

        {/* LOGO */}
        <div className="flex justify-start">
          <Link to="/" className="flex items-center">
            <img
              src="/assets/echojham.logo2.png"
              alt="Echo Jham Logo"
              className="h-10 w-auto object-contain"
            />
          </Link>
        </div>

        {/* NAV LINKS */}
        <div className="hidden lg:flex justify-center items-center gap-10 text-[11px] font-bold uppercase tracking-[0.3em]">

          <Link
            to="/"
            className={`transition ${
              isActive("/")
                ? "text-white border-b-2 border-white pb-1"
                : "text-white/70 hover:text-white"
            }`}
          >
            Home
          </Link>

          <Link
            to="/events"
            className={`transition ${
              isEventsActive
                ? "text-white border-b-2 border-white pb-1"
                : "text-white/70 hover:text-white"
            }`}
          >
            Events
          </Link>

          <Link
            to="/tickets"
            className={`transition ${
              isActive("/tickets")
                ? "text-white border-b-2 border-white pb-1"
                : "text-white/70 hover:text-white"
            }`}
          >
            My Ticket
          </Link>

          <Link
            to="/about"
            className={`transition ${
              isActive("/about")
                ? "text-white border-b-2 border-white pb-1"
                : "text-white/70 hover:text-white"
            }`}
          >
            About
          </Link>

        </div>

        {/* RIGHT SIDE */}
        <div className="flex justify-end items-center gap-2 ml-auto lg:justify-self-end">

          {/* Search — hidden on event details (list filter only) */}
          {!isEventDetailsPage && (
            <>
              <form className="relative hidden xl:block -translate-x-2" onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  className="bg-white/10 border border-white/20 text-white pl-4 pr-10 py-1.5 rounded-full text-[10px] focus:outline-none w-60"
                />
                <button type="submit" className="absolute right-3 top-2 text-white/50 hover:text-white/80">
                  <Search className="h-3.5 w-3.5" />
                </button>
              </form>

              <button
                type="button"
                onClick={() => {
                  setMobileSearchOpen((prev) => !prev);
                  setMenuOpen(false);
                }}
                className="xl:hidden text-white/70 hover:text-white p-2"
                aria-label="Toggle search"
              >
                <Search size={20} />
              </button>
            </>
          )}

          {/* User */}
          <button
            type="button"
            onClick={() => navigate(hasUserSession ? "/profile" : "/login")}
            className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white text-brand-maroon border border-white/20 overflow-hidden transition"
            aria-label={hasUserSession ? "Go to profile" : "Go to login"}
          >
            {user ? (
              <span className="text-[10px] font-black uppercase text-white">
                {user.displayName
                  ? user.displayName.charAt(0)
                  : user.email.charAt(0)}
              </span>
            ) : (
              <User className="h-4 w-4 text-white" />
            )}
          </button>

          {/* MOBILE MENU BUTTON */}
          <button
            onClick={() => {
              setMenuOpen((prev) => !prev);
              setMobileSearchOpen(false);
            }}
            className="lg:hidden text-white p-2"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

        </div>
      </div>

      {/* MOBILE/TABLET SEARCH */}
      {!isEventDetailsPage && mobileSearchOpen && (
        <div className="xl:hidden border-t border-white/10 px-4 pb-3 pt-3">
          <form className="relative" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder="Search events or tickets..."
              value={searchValue}
              onChange={handleSearchChange}
              className="w-full bg-white/10 border border-white/20 text-white pl-4 pr-10 py-2 rounded-full text-xs focus:outline-none"
            />
            <button
              type="submit"
              className="absolute right-3 top-2.5 text-white/60 hover:text-white"
              aria-label="Submit search"
            >
              <Search className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}

      {/* MOBILE MENU */}
      {menuOpen && (
        <div className="lg:hidden bg-brand-maroon border-t border-white/10 px-6 py-6 flex flex-col gap-6 text-sm font-bold uppercase tracking-widest">

          <Link
            to="/"
            onClick={() => setMenuOpen(false)}
            className={isActive("/") ? "text-white" : "text-white/80"}
          >
            Home
          </Link>

          <Link
            to="/events"
            onClick={() => setMenuOpen(false)}
            className={isEventsActive ? "text-white" : "text-white/80"}
          >
            Events
          </Link>

          <Link
            to="/tickets"
            onClick={() => setMenuOpen(false)}
            className={isActive("/tickets") ? "text-white" : "text-white/80"}
          >
            My Ticket
          </Link>

          <Link
            to="/about"
            onClick={() => setMenuOpen(false)}
            className={isActive("/about") ? "text-white" : "text-white/80"}
          >
            About
          </Link>

        </div>
      )}
    </nav>
  );
};

export default Navbar;